import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowDownRight } from "lucide-react";

// Bright, sun-lit clips of Israeli cities (Pexels, hot-linkable)
const CLIPS = [
  {
    src: "https://videos.pexels.com/video-files/11471476/11471476-hd_1920_1080_30fps.mp4",
    poster: "https://images.pexels.com/videos/11471476/beach-drone-hotels-israel-11471476.jpeg?auto=compress&cs=tinysrgb&w=1920",
    label: "Tel Aviv beach",
  },
  {
    type: "image",
    src: "https://images.unsplash.com/photo-1678133987650-75a5856da227?auto=format&fit=crop&w=2400&q=80",
    poster: "https://images.unsplash.com/photo-1678133987650-75a5856da227?auto=format&fit=crop&w=1600&q=70",
    label: "Jerusalem",
  },
  {
    src: "https://videos.pexels.com/video-files/30166493/12935823_3840_2160_30fps.mp4",
    poster: "https://images.pexels.com/videos/30166493/the-marina-aerial-view-in-herzliya-30166493.jpeg?auto=compress&cs=tinysrgb&w=1920",
    label: "Herzliya marina",
  },
  {
    src: "https://videos.pexels.com/video-files/31341238/13376676_3840_2160_60fps.mp4",
    poster: "https://images.pexels.com/videos/31341238/4k-aerial-4k-drone-aerial-aerial-city-31341238.jpeg?auto=compress&cs=tinysrgb&w=1920",
    label: "Netanya",
  },
];

const SWAP_MS = 11000;

const HEADLINE_WORDS = ["Your", "home", "in", "Israel."];
const HEADLINE_2 = ["Shared", "or", "owned", "entirely.", "Managed", "completely."];

export default function Hero() {
  const [active, setActive] = useState(0);
  const videoRefs = useRef([]);

  // Ensure each video plays (autoplay can pause hidden tracks in some browsers)
  useEffect(() => {
    videoRefs.current.forEach((v) => {
      if (!v) return;
      v.muted = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    });
  }, []);

  // When active changes, make sure that video is playing (some browsers pause hidden videos)
  useEffect(() => {
    const v = videoRefs.current[active];
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [active]);

  // Cross-fade between clips
  useEffect(() => {
    const id = setInterval(() => {
      setActive((i) => (i + 1) % CLIPS.length);
    }, SWAP_MS);
    return () => clearInterval(id);
  }, []);

  const scrollTo = (id) => () => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden text-[var(--bone)]"
    >
      {/* Background media stack (mix of video + still image with Ken Burns) */}
      <div className="absolute inset-0 z-0 bg-[var(--ink)]">
        {CLIPS.map((clip, i) => {
          const isActive = active === i;
          if (clip.type === "image") {
            return (
              <img
                key={clip.src}
                src={clip.src}
                alt={clip.label}
                data-testid={`hero-image-${i}`}
                className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[1800ms] ease-in-out"
                style={{
                  opacity: isActive ? 1 : 0,
                  filter: "saturate(1.18) brightness(1.06) contrast(1.02)",
                  animation: isActive ? "ken-burns 16s ease-out forwards" : "none",
                  transformOrigin: "center center",
                }}
                loading={i === 0 ? "eager" : "lazy"}
              />
            );
          }
          return (
            <video
              key={clip.src}
              ref={(el) => (videoRefs.current[i] = el)}
              data-testid={i === 0 ? "hero-video" : `hero-video-${i}`}
              className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[1800ms] ease-in-out"
              style={{
                opacity: isActive ? 1 : 0,
                filter: "saturate(1.18) brightness(1.06) contrast(1.02)",
              }}
              src={clip.src}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster={clip.poster}
            />
          );
        })}
      </div>

      {/* Lightly tinted overlays — keep footage bright while preserving legibility */}
      {/* top tint for nav legibility */}
      <div className="absolute inset-x-0 top-0 z-10 h-40 bg-gradient-to-b from-[var(--ink)]/40 to-transparent pointer-events-none" />
      {/* gentle warm wash across the middle */}
      <div className="absolute inset-0 z-10 bg-[linear-gradient(180deg,rgba(10,17,40,0.08)_0%,rgba(10,17,40,0.0)_30%,rgba(10,17,40,0.0)_55%,rgba(10,17,40,0.55)_100%)] pointer-events-none" />
      {/* subtle warm color cast to feel sun-lit */}
      <div className="absolute inset-0 z-10 bg-[rgba(197,160,89,0.05)] mix-blend-overlay pointer-events-none" />
      <div className="absolute inset-0 z-10 grain pointer-events-none" />

      {/* Content */}
      <div className="relative z-20 container mx-auto px-6 md:px-12 lg:px-16 max-w-[1400px] min-h-[100svh] flex flex-col justify-between pt-32 md:pt-36 pb-12">
        <div className="max-w-5xl">
          <div
            data-testid="hero-eyebrow"
            className="mb-7 md:mb-10 flex items-center gap-4 md:gap-5 font-sans uppercase font-black text-lg sm:text-xl md:text-2xl lg:text-[1.75rem]"
            style={{
              color: "#E87A4C",
              letterSpacing: "0.16em",
              textShadow: "0 2px 24px rgba(10,17,40,0.75), 0 1px 2px rgba(10,17,40,0.55)",
            }}
          >
            <span aria-hidden className="inline-block h-0.5 w-10 md:w-14 bg-[#E87A4C] shrink-0" />
            <span className="leading-tight">Fractional ownership to full ownership · Israel</span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif-display text-[2.65rem] sm:text-6xl md:text-7xl lg:text-[5.6rem] leading-[1.02] tracking-tighter text-[var(--bone)]"
            style={{ textShadow: "0 2px 36px rgba(10,17,40,0.55), 0 1px 2px rgba(10,17,40,0.4)" }}
          >
            <span className="block word-rise">
              {HEADLINE_WORDS.map((w, i) => (
                <span key={i} style={{ animationDelay: `${0.1 + i * 0.08}s` }}>
                  {w}{i < HEADLINE_WORDS.length - 1 ? "\u00A0" : ""}
                </span>
              ))}
            </span>
            <span className="block word-rise italic text-[var(--bone)]/95">
              {HEADLINE_2.map((w, i) => (
                <span key={i} style={{ animationDelay: `${0.55 + i * 0.08}s` }}>
                  {w}{i < HEADLINE_2.length - 1 ? "\u00A0" : ""}
                </span>
              ))}
            </span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-7 md:mt-9 max-w-3xl text-xl md:text-2xl lg:text-[1.65rem] leading-snug font-semibold text-[var(--bone)]"
            style={{ textShadow: "0 2px 24px rgba(10,17,40,0.7), 0 1px 2px rgba(10,17,40,0.5)" }}
          >
            From a 25% share to full sole ownership — a curated path to a premium apartment in
            Israel, with the right partners (or none), governed by clear legal frameworks and
            managed end-to-end so your only job is to arrive and feel at home.
          </p>

          <div className="mt-9 md:mt-12 flex flex-wrap items-center gap-4">
            <Button
              data-testid="hero-register-btn"
              onClick={scrollTo("#register")}
              className="rounded-full h-12 md:h-14 px-7 md:px-9 bg-[var(--bone)] text-[var(--ink)] hover:bg-white text-sm md:text-base font-medium tracking-wide transition-transform hover:-translate-y-0.5 shadow-[0_8px_30px_rgba(10,17,40,0.35)]"
            >
              Register Your Interest
              <ArrowDownRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              data-testid="hero-howitworks-btn"
              variant="ghost"
              onClick={scrollTo("#how")}
              className="rounded-full h-12 md:h-14 px-7 md:px-9 border border-[var(--bone)]/40 backdrop-blur-sm bg-[var(--ink)]/10 text-[var(--bone)] hover:bg-[var(--bone)]/15 hover:text-[var(--bone)] text-sm md:text-base font-medium tracking-wide"
            >
              How it works
            </Button>
          </div>
        </div>

        {/* Bottom strip — current city + countries */}
        <div className="flex items-end justify-between gap-6 md:gap-10 text-[var(--bone)]/85">
          <div className="flex items-center gap-3 md:gap-4">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--gold)] animate-pulse" />
            <span className="text-overline">Now showing</span>
            <span
              key={active}
              data-testid="hero-city-label"
              className="font-serif-display italic text-base md:text-xl tracking-wide"
              style={{ animation: "word-rise 0.6s ease forwards" }}
            >
              {CLIPS[active].label}
            </span>
          </div>
          <div className="hidden md:flex font-serif-display text-base md:text-xl tracking-wide items-center gap-5 text-[var(--bone)]/85">
            <span>Australia</span><span className="text-[var(--gold)]">·</span>
            <span>United Kingdom</span><span className="text-[var(--gold)]">·</span>
            <span>France</span><span className="text-[var(--gold)]">·</span>
            <span>Canada</span><span className="text-[var(--gold)]">·</span>
            <span>USA</span><span className="text-[var(--gold)]">·</span>
            <span>South Africa</span>
          </div>
        </div>
      </div>
    </section>
  );
}
